import type { LucideIcon } from "lucide-react";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

export type WorkspaceHeaderMetric = {
  label: string;
  value: ReactNode;
  icon: LucideIcon;
  tone?: "default" | "amber" | "rose" | "emerald";
};

type Props = {
  eyebrow: string;
  title: string;
  description: string;
  icon: LucideIcon;
  metrics: WorkspaceHeaderMetric[];
  loading?: boolean;
  actions?: ReactNode;
};

const metricToneClasses = {
  default: "text-[#0b4a2f]",
  amber: "text-amber-600",
  rose: "text-rose-600",
  emerald: "text-emerald-600",
} as const;

export function WorkspaceHeader({
  eyebrow,
  title,
  description,
  icon: Icon,
  metrics,
  loading = false,
  actions,
}: Props) {
  return (
    <section
      className="dashboard-fade-up relative overflow-hidden rounded-[30px] border border-white/80 px-5 py-5 shadow-[0_24px_70px_rgba(15,23,42,.11)] sm:px-6"
      style={{
        background:
          "radial-gradient(circle at 8% 0%, rgba(0, 208, 156, 0.18), transparent 28%), linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(239, 248, 244, 0.92))",
      }}
    >
      <div className="pointer-events-none absolute -right-[92px] top-[-92px] h-[260px] w-[260px] rounded-full bg-[#00d09c]/18 blur-[6px]" />
      <div className="pointer-events-none absolute bottom-[-150px] left-[14%] h-[280px] w-[280px] rounded-full bg-[#0b4a2f]/12 blur-[10px]" />

      <div className="relative z-10 grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(360px,500px)] xl:items-center">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#00d09c]/20 bg-[#00d09c]/9 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.17em] text-[#0b4a2f] shadow-[0_8px_22px_rgba(11,74,47,.06)]">
            <Icon size={14} />
            {eyebrow}
          </div>

          <h1 className="mt-3 text-3xl font-black tracking-[-0.055em] text-slate-950 lg:text-4xl">
            {title}
          </h1>

          <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
            {description}
          </p>

          {actions ? <div className="mt-4 flex flex-wrap gap-2.5">{actions}</div> : null}
        </div>

        <div className={metrics.length === 1 ? "grid grid-cols-1 gap-2.5" : "grid grid-cols-2 gap-2.5"}>
          {metrics.slice(0, 4).map((metric) => {
            const MetricIcon = metric.icon;
            const tone = metric.tone ?? "default";

            return (
              <div
                key={metric.label}
                className="rounded-[20px] border border-[#dfe8e4] bg-white/90 px-3.5 py-3 shadow-[0_10px_28px_rgba(15,23,42,.055)]"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                    {metric.label}
                  </span>

                  {loading ? (
                    <Loader2 size={14} className="animate-spin text-slate-400" />
                  ) : (
                    <MetricIcon size={15} className={metricToneClasses[tone]} />
                  )}
                </div>

                <div className="mt-1.5 truncate text-xl font-black text-slate-950">
                  {loading ? "—" : metric.value}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
